import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { 
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Percent,
  Wallet,
  TrendingUp,
  Edit,
  Plus,
  ArrowDownRight,
  ArrowUpRight,
  RefreshCw,
  StickyNote,
  Building2,
  Smartphone,
  KeyRound,
  Copy,
  Check,
  FileText,
  Download,
  X,
  Trash2,
  Upload,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Component to preview document with authentication
function DocumentPreview({ documentUrl }) {
  const [imageSrc, setImageSrc] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    if (!documentUrl || !documentUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      setPreviewLoading(false);
      return;
    }

    const loadImage = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${documentUrl}`, {
          responseType: 'blob'
        });
        const blobUrl = URL.createObjectURL(response.data);
        setImageSrc(blobUrl);
      } catch (err) {
        setError(true);
      } finally {
        setPreviewLoading(false);
      }
    };

    loadImage();

    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [documentUrl]);

  if (!documentUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
    return null;
  }

  if (previewLoading) {
    return (
      <div className="mt-3 flex items-center justify-center h-20">
        <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
      </div>
    );
  }

  if (error || !imageSrc) {
    return null;
  }

  return (
    <div className="mt-3">
      <img
        src={imageSrc}
        alt="Documento"
        className="max-h-48 rounded-lg border border-white/10"
      />
    </div>
  );
}

export default function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showEditTransactionModal, setShowEditTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [newPassword, setNewPassword] = useState(null);
  const [copied, setCopied] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [newPayment, setNewPayment] = useState({
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [editTransaction, setEditTransaction] = useState({
    description: '',
    amount: '',
    date: ''
  });

  const handleUploadDocument = async (file) => {
    if (!file) return;
    
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('document', file);
    
    try {
      const response = await axios.post(`${API_URL}/admin/users/${userId}/upload-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento carregado com sucesso');
      setUser(prev => ({...prev, id_document_url: response.data.document_url}));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar documento');
    } finally {
      setUploadingDoc(false);
    }
  };

  // View document with authentication
  const handleViewDocument = async (documentUrl) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${documentUrl}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      toast.error('Erro ao abrir documento');
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    try {
      const [userRes, transactionsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users/${userId}`),
        axios.get(`${API_URL}/admin/users/${userId}/transactions`)
      ]);
      setUser(userRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      // Error silenced: 'Failed to fetch user:', error);
      toast.error('Utilizador não encontrado');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    
    if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) {
      toast.error('Introduza um montante válido');
      return;
    }

    setPaymentLoading(true);
    try {
      await axios.post(`${API_URL}/admin/users/${userId}/add-profit`, {
        amount: parseFloat(newPayment.amount),
        description: newPayment.description || `Lucro registado - ${newPayment.date}`,
        date: newPayment.date
      });

      toast.success('Lucro/Pagamento registado com sucesso');
      setShowAddPaymentModal(false);
      setNewPayment({
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registar pagamento');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setResetPasswordLoading(true);
    try {
      const response = await axios.post(`${API_URL}/admin/users/${userId}/reset-password`);
      setNewPassword(response.data.new_password);
      toast.success('Senha redefinida com sucesso');
      fetchData(); // Refresh to show updated admin notes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao redefinir senha');
      setShowResetPasswordModal(false);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const copyPassword = () => {
    if (newPassword) {
      navigator.clipboard.writeText(newPassword);
      setCopied(true);
      toast.success('Senha copiada!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const closeResetPasswordModal = () => {
    setShowResetPasswordModal(false);
    setNewPassword(null);
    setCopied(false);
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users/${userId}/statement/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = user?.name?.replace(/\s+/g, '_').toLowerCase() || 'cliente';
      link.download = `extrato_${safeName}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('PDF descarregado com sucesso!');
    } catch (error) {
      // Error silenced: 'Failed to download PDF:', error);
      toast.error('Erro ao descarregar PDF');
    }
  };

  const openEditTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setEditTransaction({
      description: transaction.description || '',
      amount: transaction.amount || '',
      date: transaction.created_at ? transaction.created_at.split('T')[0] : ''
    });
    setShowEditTransactionModal(true);
  };

  const handleUpdateTransaction = async (e) => {
    e.preventDefault();
    if (!selectedTransaction) return;

    setEditLoading(true);
    try {
      const updates = {};
      if (editTransaction.description !== selectedTransaction.description) {
        updates.description = editTransaction.description;
      }
      if (parseFloat(editTransaction.amount) !== selectedTransaction.amount) {
        updates.amount = parseFloat(editTransaction.amount);
      }
      if (editTransaction.date !== selectedTransaction.created_at?.split('T')[0]) {
        updates.date = editTransaction.date;
      }

      if (Object.keys(updates).length > 0) {
        await axios.put(`${API_URL}/admin/transactions/${selectedTransaction.id}`, updates);
        toast.success('Transação atualizada com sucesso');
        fetchData();
      }
      setShowEditTransactionModal(false);
      setSelectedTransaction(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar transação');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Tem a certeza que deseja eliminar esta transação? Esta ação irá ajustar o saldo/rendimentos do utilizador.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/admin/transactions/${transactionId}`);
      toast.success('Transação eliminada com sucesso');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar transação');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit':
      case 'credit':
        return <ArrowDownRight className="w-4 h-4 text-emerald-400" />;
      case 'withdrawal':
      case 'debit':
        return <ArrowUpRight className="w-4 h-4 text-red-400" />;
      case 'utility_payment':
      case 'profit':
        return <TrendingUp className="w-4 h-4 text-emerald-400" />;
      case 'reinvestment':
        return <RefreshCw className="w-4 h-4 text-blue-400" />;
      default:
        return <CreditCard className="w-4 h-4 text-slate-400" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'deposit':
      case 'utility_payment':
      case 'credit':
      case 'reinvestment':
      case 'profit':
        return 'text-emerald-400';
      case 'withdrawal':
      case 'debit':
        return 'text-red-400';
      default:
        return 'text-white';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="user-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-400" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Outfit']">{user?.name}</h1>
            <p className="text-slate-400 text-sm">{user?.email}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Dialog open={showAddPaymentModal} onOpenChange={setShowAddPaymentModal}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-600 rounded-xl flex items-center gap-2" data-testid="add-profit-btn">
                <Plus className="w-5 h-5" />
                Adicionar Rentabilidade
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#252F38] border-white/10 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white font-['Outfit']">Adicionar Rentabilidade / Lucro</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddPayment} className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-300">Montante (€)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={newPayment.amount}
                    onChange={(e) => {
                      const value = e.target.value.replace(',', '.');
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setNewPayment({ ...newPayment, amount: value });
                      }
                    }}
                    placeholder="0.00"
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                    required
                    data-testid="profit-amount"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Data do Pagamento</Label>
                  <Input
                    type="date"
                    value={newPayment.date}
                    onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                    data-testid="profit-date"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Descrição (opcional)</Label>
                  <Input
                    value={newPayment.description}
                    onChange={(e) => setNewPayment({ ...newPayment, description: e.target.value })}
                    placeholder="Ex: Pagamento de Janeiro 2024"
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                    data-testid="profit-description"
                  />
                </div>
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                  <p className="text-sm text-emerald-400">
                    Este valor será adicionado aos rendimentos acumulados do cliente e registado no histórico de transações.
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={paymentLoading}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-xl"
                  data-testid="submit-profit"
                >
                  {paymentLoading ? 'A processar...' : 'Registar Lucro'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Reset Password Modal */}
          <Dialog open={showResetPasswordModal} onOpenChange={(open) => {
            if (!open) closeResetPasswordModal();
            else setShowResetPasswordModal(true);
          }}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/30 text-amber-400 rounded-xl flex items-center gap-2"
                data-testid="reset-password-btn"
              >
                <KeyRound className="w-5 h-5" />
                Redefinir Senha
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#252F38] border-white/10 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white font-['Outfit']">Redefinir Senha</DialogTitle>
              </DialogHeader>
              
              {!newPassword ? (
                <div className="space-y-4 mt-4">
                  <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                    <p className="text-amber-400 text-sm">
                      Tem a certeza que deseja redefinir a senha de <strong>{user?.name}</strong>?
                    </p>
                    <p className="text-slate-400 text-xs mt-2">
                      Uma nova senha será gerada e enviada por email ao cliente.
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={closeResetPasswordModal}
                      className="flex-1 bg-white/5 hover:bg-white/10 border-white/10 rounded-xl"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleResetPassword}
                      disabled={resetPasswordLoading}
                      className="flex-1 bg-amber-500 hover:bg-amber-600 rounded-xl"
                      data-testid="confirm-reset-password"
                    >
                      {resetPasswordLoading ? 'A processar...' : 'Confirmar'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 mt-4">
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                    <p className="text-emerald-400 text-sm mb-3">
                      Senha redefinida com sucesso!
                    </p>
                    <div className="flex items-center gap-2 p-3 bg-[#161C22] rounded-lg">
                      <p className="text-white font-mono text-lg flex-1">{newPassword}</p>
                      <button
                        onClick={copyPassword}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        data-testid="copy-password-btn"
                      >
                        {copied ? (
                          <Check className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <Copy className="w-5 h-5 text-slate-400" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      Esta senha também foi enviada por email e guardada nas notas do admin.
                    </p>
                  </div>
                  <Button
                    onClick={closeResetPasswordModal}
                    className="w-full bg-blue-500 hover:bg-blue-600 rounded-xl"
                  >
                    Fechar
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          <Button
            onClick={handleDownloadPDF}
            variant="outline"
            className="bg-purple-500/10 hover:bg-purple-500/20 border-purple-500/30 text-purple-400 rounded-xl flex items-center gap-2"
            data-testid="download-pdf-btn"
          >
            <FileText className="w-5 h-5" />
            PDF
          </Button>

          <Button
            onClick={() => navigate(`/admin/users/${userId}/edit`)}
            variant="outline"
            className="bg-white/5 hover:bg-white/10 border-white/10 rounded-xl flex items-center gap-2"
          >
            <Edit className="w-5 h-5" />
            Editar
          </Button>
          <Button
            onClick={() => navigate(`/admin/wallet/${userId}`)}
            className="bg-blue-500 hover:bg-blue-600 rounded-xl flex items-center gap-2"
          >
            <Wallet className="w-5 h-5" />
            Gerir Wallet
          </Button>
        </div>
      </div>

      {/* User Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 text-slate-400 mb-3">
            <Wallet className="w-5 h-5 text-blue-400" />
            <span className="text-sm">Saldo Atual</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatCurrency(user?.balance)}</p>
        </div>
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 text-slate-400 mb-3">
            <Percent className="w-5 h-5 text-emerald-400" />
            <span className="text-sm">Taxa de Rendimento</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400">{user?.profit_percentage}%</p>
        </div>
        <div className="glass-card p-6 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/20">
          <div className="flex items-center gap-3 text-emerald-400 mb-3">
            <TrendingUp className="w-5 h-5" />
            <span className="text-sm font-medium">Receberá Este Mês</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400">
            {formatCurrency(user?.expected_this_month || 0)}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {user?.days_invested || 0} de {user?.days_in_month || 30} dias ({user?.profit_percentage}%)
          </p>
        </div>
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 text-slate-400 mb-3">
            <TrendingUp className="w-5 h-5 text-purple-400" />
            <span className="text-sm">Rendimentos Acumulados</span>
          </div>
          <p className="text-2xl font-bold text-purple-400">{formatCurrency(user?.accumulated_returns)}</p>
        </div>
      </div>

      {/* User Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Personal Info */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-400" />
            Dados Pessoais
          </h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-slate-500" />
              <div>
                <p className="text-xs text-slate-500">Email</p>
                <p className="text-white">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-slate-500" />
              <div>
                <p className="text-xs text-slate-500">Telefone</p>
                <p className="text-white">{user?.phone || 'Não definido'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <FileText className="w-4 h-4 text-slate-500" />
              <div>
                <p className="text-xs text-slate-500">NIF (Nº Contribuinte)</p>
                <p className="text-white font-mono">{user?.nif || 'Não definido'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-slate-500" />
              <div>
                <p className="text-xs text-slate-500">Morada</p>
                <p className="text-white">{user?.address || 'Não definida'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <CreditCard className="w-4 h-4 text-slate-500" />
              <div>
                <p className="text-xs text-slate-500">IBAN</p>
                <p className="text-white font-mono text-sm">{user?.iban || 'Não definido'}</p>
              </div>
            </div>
            {user?.bank_name && (
              <div className="flex items-center gap-3">
                <Building2 className="w-4 h-4 text-slate-500" />
                <div>
                  <p className="text-xs text-slate-500">Banco</p>
                  <p className="text-white">{user.bank_name}</p>
                </div>
              </div>
            )}
            {user?.revolut_account && (
              <div className="flex items-center gap-3">
                <Smartphone className="w-4 h-4 text-slate-500" />
                <div>
                  <p className="text-xs text-slate-500">Revolut</p>
                  <p className="text-white">{user.revolut_account}</p>
                </div>
              </div>
            )}
          </div>

          {/* ID Document Section */}
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="flex items-center gap-2 text-blue-400 mb-3">
              <FileText className="w-4 h-4" />
              <span className="text-sm font-medium">Documento de Identificação</span>
            </div>
            <div className="bg-[#1D262D] border border-white/10 rounded-xl p-4">
              {user?.id_document_url ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-white text-sm">Documento anexado</span>
                    <button
                      onClick={() => handleViewDocument(user.id_document_url)}
                      className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ver
                    </button>
                  </div>
                  <DocumentPreview documentUrl={user.id_document_url} />
                </>
              ) : (
                <p className="text-amber-400 text-sm">Sem documento anexado</p>
              )}
              <div className="mt-3 pt-3 border-t border-white/5">
                <label className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-lg cursor-pointer transition-colors">
                  {uploadingDoc ? (
                    <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                  ) : (
                    <Upload className="w-4 h-4 text-blue-400" />
                  )}
                  <span className="text-blue-400 text-xs">
                    {uploadingDoc ? 'A carregar...' : (user?.id_document_url ? 'Substituir' : 'Carregar')}
                  </span>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.webp"
                    className="hidden"
                    disabled={uploadingDoc}
                    onChange={(e) => handleUploadDocument(e.target.files[0])}
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Admin Notes */}
          {user?.admin_notes && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center gap-2 text-amber-400 mb-2">
                <StickyNote className="w-4 h-4" />
                <span className="text-sm font-medium">Notas do Admin</span>
              </div>
              <p className="text-slate-300 text-sm bg-amber-500/5 p-3 rounded-lg border border-amber-500/10">
                {user.admin_notes}
              </p>
            </div>
          )}
        </div>

        {/* Transactions History */}
        <div className="glass-card p-6 lg:col-span-2">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            Histórico de Transações
          </h2>
          
          {transactions.length > 0 ? (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {transactions.map((transaction) => (
                <div 
                  key={transaction.id}
                  className="flex items-center justify-between p-3 bg-white/5 rounded-xl group"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      transaction.type === 'withdrawal' || transaction.type === 'debit'
                        ? 'bg-red-500/10'
                        : 'bg-emerald-500/10'
                    }`}>
                      {getTransactionIcon(transaction.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{transaction.description}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(transaction.created_at).toLocaleDateString('pt-PT', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${getTransactionColor(transaction.type)}`}>
                      {transaction.type === 'withdrawal' || transaction.type === 'debit' ? '-' : '+'}
                      {formatCurrency(transaction.amount)}
                    </span>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button
                        onClick={() => openEditTransaction(transaction)}
                        className="p-1.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 transition-colors"
                        title="Editar"
                        data-testid={`edit-transaction-${transaction.id}`}
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteTransaction(transaction.id)}
                        className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                        title="Eliminar"
                        data-testid={`delete-transaction-${transaction.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Sem transações registadas</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Transaction Modal */}
      <Dialog open={showEditTransactionModal} onOpenChange={setShowEditTransactionModal}>
        <DialogContent className="bg-[#252F38] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit']">Editar Transação</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateTransaction} className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Descrição</Label>
              <Input
                value={editTransaction.description}
                onChange={(e) => setEditTransaction({ ...editTransaction, description: e.target.value })}
                placeholder="Descrição da transação"
                className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                data-testid="edit-transaction-description"
              />
            </div>
            <div>
              <Label className="text-slate-300">Montante (€)</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={editTransaction.amount}
                onChange={(e) => {
                  const value = e.target.value.replace(',', '.');
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setEditTransaction({ ...editTransaction, amount: value });
                  }
                }}
                placeholder="0.00"
                className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                data-testid="edit-transaction-amount"
              />
            </div>
            <div>
              <Label className="text-slate-300">Data</Label>
              <Input
                type="date"
                value={editTransaction.date}
                onChange={(e) => setEditTransaction({ ...editTransaction, date: e.target.value })}
                className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                data-testid="edit-transaction-date"
              />
            </div>
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
              <p className="text-sm text-amber-400">
                Ao alterar o montante de uma transação de lucro, os rendimentos acumulados do utilizador serão ajustados automaticamente.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditTransactionModal(false)}
                className="flex-1 bg-white/5 hover:bg-white/10 border-white/10 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={editLoading}
                className="flex-1 bg-blue-500 hover:bg-blue-600 rounded-xl"
                data-testid="save-transaction-btn"
              >
                {editLoading ? 'A guardar...' : 'Guardar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
